import React from "react";
import InnerLayout from "../../../layouts/inner-layout";
import { Form, Formik } from "formik";
import AddButton from "../../../buttons/button";
import { useLocation, useNavigate } from "react-router-dom";
import SearchAddFeild from "../../../forms/search-add-field";
import { fetchData } from "../../../helper-components/helper-function";
import CloseIcon from "../../../../assets/icons/close-icon";
import errorToast from "../../../helper-components/error-toast";
import successToast from "../../../helper-components/success-toast";
import { useDispatch } from "react-redux";
import SelectField from "../../../forms/select-field";
import { VendorMappingAction } from "../../../../redux/actions/settings-action";
import { purchaseProductDetailsAction } from "../../../../redux/actions/purchase-action";

/**
 * Vendor-mapping CSV review page.
 *
 * Reached after a CSV is uploaded on the vendor mapping screen and verified
 * through the shared GRN verify API. It shows each parsed vendor item with its
 * auto-suggested system product + UOM, lets the user fix mismatches, then
 * persists everything via the add-vendor-mapping API on Import.
 */
const VendorMappingCsv = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state?.data || {};
  const vendor = location.state?.vendor || {};

  const initialValues = { ...data };

  // Build the add-vendor-mapping payload, grouping every CSV row by its vendor
  // item code so multiple vendor UOMs collapse into one item's mapping list.
  const buildPayload = (values) => {
    const cm = values?.columnMapping || {};
    const grouped = {};

    (values?.itemData || []).forEach((item) => {
      const vendorCode = (item?.[cm.item_code] ?? "").toString().trim();
      const vendorName = (item?.[cm.item_name] ?? "").toString();
      // Vendor UOM as it appears in the vendor's CSV column. When the file has
      // no UOM column (e.g. the pack is embedded in the item name), that cell is
      // blank, so fall back to the backend-translated vendor_uom and finally to
      // the resolved/selected system UOM so the mapping is still captured.
      const vendorUom = (
        item?.[cm.item_uom] ||
        item?.vendor_uom ||
        item?.item_uom ||
        ""
      )
        .toString()
        .trim();
      if (!vendorCode) return;

      if (!grouped[vendorCode]) {
        grouped[vendorCode] = {
          vendor_item_code: vendorCode,
          vendor_item_name: vendorName,
          system_item_id: item?.item_id || "",
          system_item_code: item?.item_code || "",
          system_item_name: item?.item_name || "",
          mapping: [],
        };
      }

      grouped[vendorCode].mapping.push({
        vendor_uom: vendorUom,
        system_uom: item?.item_uom || "",
        system_uom_id: item?.item_uom_id || "",
        uom_mapping_id: null,
      });
    });

    return {
      vendor_code: vendor?.cmr_code || "",
      vendor_name: vendor?.cmr_name || "",
      vendor_id: vendor?.cmr_id || "",
      data: Object.values(grouped),
    };
  };

  const handleSubmit = (values, actions) => {
    const missingSystemItem = (values?.itemData || []).some((item) => !item.item_id);
    const missingUOM = (values?.itemData || []).some((item) => !item.item_uom_id);

    if (missingSystemItem) {
      errorToast("System Product is required for all rows!");
      actions.setSubmitting(false);
      return;
    }
    if (missingUOM) {
      errorToast("System UOM is required for all rows!");
      actions.setSubmitting(false);
      return;
    }

    dispatch(
      VendorMappingAction(
        buildPayload(values),
        () => {
          successToast("Mapping Successful");
          navigate("/settings/bussiness-catalouge/new");
        },
        (err) => {
          actions.setSubmitting(false);
          errorToast(err?.data?.message || "Mapping Unsuccessful");
        }
      )
    );
  };

  return (
    <InnerLayout backBtn={true} title="Vendor Mapping CSV Import">
      <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => (
          <Form className="h-full">
            <div
              className="flex gap-3 justify-between overflow-auto"
              style={{
                maxHeight: `calc(100% - 4.5rem)`,
                height: `calc(100% - 4.5rem)`,
              }}
            >
              <div className="min-w-[100%] border-EAECF0 border mb-2 h-fit scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                <table className="my-tab">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-F2F4F5">
                      {tableHeading.map((heading, index) => (
                        <th
                          key={index}
                          className="border-b-EAECF0 border-b h-[3rem] text-left px-2 whitespace-nowrap text-sm fw_700 text-475467 bg-F2F4F5"
                        >
                          {heading?.toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {values?.itemData?.map((item, index) => (
                      <tr key={index}>
                        <td className="text-sm h-[3.5rem] px-3 border-b-EAECF0 border-b fw_400 text-secondary">
                          {index + 1}
                        </td>
                        <td className="text-sm h-[3.5rem] px-3 border-b-EAECF0 border-b fw_400 text-secondary text-nowrap">
                          {item?.[values?.columnMapping?.item_code] || "N/A"}
                        </td>
                        <td className="text-sm h-[3.5rem] px-3 border-b-EAECF0 border-b fw_400 text-secondary">
                          {item?.[values?.columnMapping?.item_name] || "N/A"}
                        </td>
                        <td className="text-sm h-[3.5rem] px-3 border-b-EAECF0 border-b fw_400 text-secondary">
                          {item?.[values?.columnMapping?.item_uom] || "N/A"}
                        </td>

                        {/* System item code (resolved from the selected product) */}
                        <td className="text-sm h-[3.5rem] px-3 border-b-EAECF0 border-b fw_400 text-secondary text-nowrap">
                          {item?.item_code || "N/A"}
                        </td>

                        {/* System product (auto-suggested, editable) */}
                        <td className="text-sm h-[3.5rem] min-w-[20rem] border-b-EAECF0 border-b fw_400 text-secondary">
                          <div
                            className={`relative w-full ${
                              item?.is_item_in_db || item?.item_id
                                ? ""
                                : "border-F51D00 border-2 rounded-md"
                            }`}
                          >
                            <SearchAddFeild
                              errorClass="hidden"
                              isRequired={false}
                              value={{ label: item?.item_name, value: "" }}
                              name={`itemData[${index}].item_id`}
                              field="item_name"
                              isLabelReq={false}
                              defaultSelectClass=""
                              placeholder=""
                              fetchData={fetchData}
                              onChange={(selectedOption) => {
                                if (selectedOption?.text === "new_item") {
                                  navigate("/add-product");
                                  return;
                                }
                                const updated = [...values.itemData];
                                updated[index] = {
                                  ...updated[index],
                                  item_name: selectedOption.item_name,
                                  item_id: selectedOption.item_id,
                                  item_code: selectedOption.item_code,
                                  is_item_in_db: true,
                                  item_uom_id:
                                    selectedOption.item_pur_purchasing_uom_name_id || "",
                                  item_uom:
                                    selectedOption.item_pur_purchasing_uom_name || "",
                                };
                                setFieldValue("itemData", updated);

                                // refresh available UOMs for the newly chosen item
                                dispatch(
                                  purchaseProductDetailsAction(
                                    { item_id: selectedOption.item_id, item_uom: "" },
                                    (res) => {
                                      setFieldValue(
                                        `itemData[${index}].uom_dropdown`,
                                        res?.data?.uom_dropdown || []
                                      );
                                    },
                                    () => {}
                                  )
                                );
                              }}
                              index={index}
                              defaultInputValue={true}
                            />

                            <div
                              onClick={() => {
                                setFieldValue(`itemData[${index}].item_id`, "");
                                setFieldValue(`itemData[${index}].item_name`, "");
                                setFieldValue(`itemData[${index}].item_code`, "");
                                setFieldValue(`itemData[${index}].is_item_in_db`, false);
                                setFieldValue(`itemData[${index}].item_uom`, "");
                                setFieldValue(`itemData[${index}].item_uom_id`, "");
                                setFieldValue(`itemData[${index}].uom_dropdown`, []);
                              }}
                              className="absolute cursor-pointer z-15 right-1 top-1/2 transform -translate-y-1/2"
                            >
                              {item?.item_name && <CloseIcon width={15} height={15} />}
                            </div>
                          </div>
                        </td>

                        {/* System UOM (auto-suggested, editable) */}
                        <td className="text-sm h-[3.5rem] min-w-[9rem] px-3 fw_400 text-secondary rounded-md border-b-EAECF0 border-b">
                          <div
                            className={`${
                              item?.item_uom_id ? "" : "border-F51D00 border-2 rounded-md px-4"
                            }`}
                          >
                            <SelectField
                              handleSelected={(sel) => {
                                setFieldValue(`itemData[${index}].item_uom`, sel?.label);
                              }}
                              borderclass={item?.item_uom ? "" : "border border-F51D00"}
                              defaultSelectClass={
                                item?.item_uom
                                  ? "border-0"
                                  : "border border-F51D00 rounded-md"
                              }
                              selectClass="w-full"
                              defaultValue={item?.item_uom ? { label: item?.item_uom } : null}
                              errMsgContainerClass="hidden"
                              id={`itemData[${index}].item_uom_id`}
                              isContainer={false}
                              options={(() => {
                                const opts =
                                  item?.uom_dropdown?.map((opt) => ({
                                    label: opt?.alt_uom,
                                    value: opt?.item_id,
                                  })) || [];
                                // keep the current selection visible even if it is
                                // not part of this item's uom_dropdown
                                if (
                                  item?.item_uom_id &&
                                  !opts.some((opt) => opt.value === item?.item_uom_id)
                                ) {
                                  opts.unshift({
                                    label: item?.item_uom,
                                    value: item?.item_uom_id,
                                  });
                                }
                                return opts;
                              })()}
                              name={`itemData[${index}].item_uom_id`}
                              placeholder="Select UOM"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-between invoice-bottom-container w-full py-4 px-4 items-center h-[4rem]">
              <div className="flex gap-3 fw_600" />
              <AddButton prefixIcon={false} text="Import" type="submit" />
            </div>
          </Form>
        )}
      </Formik>
    </InnerLayout>
  );
};

export default VendorMappingCsv;

const tableHeading = [
  "SL No",
  "Vendor Prod. Code",
  "Vendor Prod. Name",
  "Vendor UOM",
  "System Code",
  "System Product",
  "System UOM",
];
